<template>
  <iframe
    ref="frame"
    class="ae-d3-insight"
    :style="{ height: height + 'px' }"
    sandbox="allow-scripts"
    referrerpolicy="no-referrer"
    :srcdoc="document"
    title="Visualization"
  />
</template>

<script>
import { TOKENS_CSS, FRAME_CSP, demoSafeRows, demoSafeInputs, jsonForScript } from './widgets/frameTokens';

const D3_SRC = 'https://cdnjs.cloudflare.com/ajax/libs/d3/7.9.0/d3.min.js';

export default {
  name: 'D3Insight',
  props: {
    code: { type: String, required: true },
    rows: { type: Array, default: () => [] },
    inputs: { type: Object, default: null },
    minHeight: { type: Number, default: 320 },
    fixedHeight: { type: Number, default: 0 },
  },
  emits: ['export'],
  data() {
    return { height: this.fixedHeight || this.minHeight };
  },
  computed: {
    document() {
      const inputs = demoSafeInputs(this.inputs || {});
      const names = Object.keys(inputs);
      const rows = this.rows && this.rows.length ? demoSafeRows(this.rows) : (names.length ? inputs[names[0]] : []);
      return `<!doctype html><html><head>
<meta charset="utf-8">
<meta http-equiv="Content-Security-Policy" content="${FRAME_CSP}">
<style>
  ${TOKENS_CSS}
  html, body { margin: 0; max-width: 100%; overflow-x: hidden; }
  body { font: 13px -apple-system, system-ui, sans-serif; color: #38352f; background: #fff; }
  #chart { width: 100%; max-width: 100%; }
  #chart svg { max-width: 100%; height: auto; display: block; }
  .err { padding: 12px; color: #c23a28; font-family: 'IBM Plex Mono', monospace; font-size: 12px; white-space: pre-wrap; }
  #kairon-tip { position: fixed; z-index: 2147483647; pointer-events: none; display: none; max-width: 320px; padding: 7px 9px; border-radius: 6px; background: var(--ink); color: #fff; font: 12px/1.4 -apple-system, system-ui, sans-serif; white-space: pre-line; box-shadow: 0 4px 14px rgba(26, 25, 24, 0.18); }
</style>
<script src="${D3_SRC}"><\/script>
</head><body>
<div id="chart"></div>
<div id="kairon-tip"></div>
<script>
(function () {
  var data = ${jsonForScript(rows)};
  var inputs = ${jsonForScript(inputs)};
  var el = document.getElementById('chart');
  var width = el.clientWidth;
  var exportData = function (spec) {
    parent.postMessage({ d3insight: 'export', spec: JSON.parse(JSON.stringify(spec || {})) }, '*');
  };
  var post = function () {
    parent.postMessage({ d3insight: 'height', value: Math.ceil(document.body.getBoundingClientRect().height) }, '*');
  };
  var fit = function () {
    el.querySelectorAll('svg').forEach(function (svg) {
      if (!svg.getAttribute('viewBox')) {
        var w = parseFloat(svg.getAttribute('width'));
        var h = parseFloat(svg.getAttribute('height'));
        if (w > 0 && h > 0) svg.setAttribute('viewBox', '0 0 ' + w + ' ' + h);
      }
      svg.removeAttribute('width');
      svg.removeAttribute('height');
      svg.setAttribute('preserveAspectRatio', 'xMinYMin meet');
    });
  };
  try {
    ${this.code}
  } catch (e) {
    el.innerHTML = '<div class="err">' + (e && e.message ? e.message : String(e)) + '<\\/div>';
  }
  fit();
  var tip = document.getElementById('kairon-tip');
  var tipTarget = null;
  var tipFor = function (node) {
    while (node && node.nodeType === 1) {
      if (node.hasAttribute('title') && node.getAttribute('title')) {
        node.setAttribute('data-tip', node.getAttribute('title'));
        node.removeAttribute('title');
      }
      if (node.getAttribute('data-tip')) return node;
      node = node.parentNode;
    }
    return null;
  };
  var placeTip = function (e) {
    var pad = 10, w = tip.offsetWidth, h = tip.offsetHeight;
    var x = e.clientX + 14, y = e.clientY + 14;
    if (x + w + pad > window.innerWidth) x = Math.max(pad, e.clientX - w - 14);
    if (y + h + pad > window.innerHeight) y = Math.max(pad, e.clientY - h - 14);
    tip.style.left = x + 'px';
    tip.style.top = y + 'px';
  };
  document.addEventListener('mouseover', function (e) {
    tipTarget = tipFor(e.target);
    if (!tipTarget) { tip.style.display = 'none'; return; }
    tip.textContent = tipTarget.getAttribute('data-tip');
    tip.style.display = 'block';
    placeTip(e);
  });
  document.addEventListener('mousemove', function (e) { if (tipTarget) placeTip(e); });
  document.addEventListener('mouseleave', function () { tipTarget = null; tip.style.display = 'none'; });
  new ResizeObserver(post).observe(document.body);
  post();
})();
<\/script>
</body></html>`;
    },
  },
  watch: {
    fixedHeight(value) {
      if (value) this.height = value;
    },
  },
  mounted() {
    window.addEventListener('message', this.onMessage);
  },
  beforeUnmount() {
    window.removeEventListener('message', this.onMessage);
  },
  methods: {
    onMessage(event) {
      const frame = this.$refs.frame;
      if (!frame || event.source !== frame.contentWindow) return;
      if ((event.data || {}).d3insight === 'export') {
        this.$emit('export', event.data.spec || {});
        return;
      }
      if (this.fixedHeight) return;
      if ((event.data || {}).d3insight === 'height') {
        this.height = Math.max(this.minHeight, Number(event.data.value) || this.minHeight);
      }
    },
  },
};
</script>

<style scoped>
.ae-d3-insight { width: 100%; border: 0; background: #ffffff; display: block; flex: none; }
</style>
